import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa';

const steps = [
  {
    id: 'account',
    number: '01',
    title: 'Create your Pinngro account',
    summary: 'Create your account to securely access your personal Pinngro experience.',
    why: (
      <p>
        Your Pinngro account gives you your own secure workspace and allows Pinngro to keep
        your preferences and account features associated with you.
      </p>
    ),
    how: (
      <p>
        Select <Link to="/signup" className="font-bold text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200">Register</Link>,
        {' '}enter the requested details and follow the on-screen instructions.
      </p>
    ),
  },
  {
    id: 'security',
    number: '02',
    title: 'Protect your account',
    summary: 'Use an authenticator app to add an extra layer of protection to your account.',
    why: (
      <p>
        An authenticator app adds a second layer of security to your account. Instead of
        relying only on your password, it generates a temporary code on your phone when
        Pinngro needs to confirm that it is really you.
      </p>
    ),
    how: (
      <p>
        Install an authenticator app on your phone and follow the Pinngro setup instructions.
        During setup, scan the QR code shown by Pinngro and enter the temporary code generated
        by the app.
      </p>
    ),
  },
  {
    id: 'brokerage',
    number: '03',
    title: 'Connect a brokerage account',
    summary: "Connect a brokerage account when you're ready to use features that require it.",
    why: (
      <p>
        A brokerage account is where investments are held and transactions are carried out by
        the brokerage. Connecting one allows Pinngro to use features that depend on a secure
        brokerage connection. During Human Beta, Pinngro does not place live external orders.
      </p>
    ),
    how: (
      <div className="space-y-2">
        <p>
          After signing in, open Account and follow the secure brokerage connection instructions.
          Pinngro will guide you through each step.
        </p>
        <p className="font-semibold text-slate-700 dark:text-slate-200">
          Don&apos;t have a brokerage account yet? That&apos;s OK — we&apos;ll explain what you need
          and guide you through the process.
        </p>
      </div>
    ),
  },
];

const GettingStartedGuide = () => {
  const [expanded, setExpanded] = useState({});

  const toggle = (key) => {
    setExpanded((current) => ({ ...current, [key]: !current[key] }));
  };

  return (
    <section
      aria-labelledby="getting-started-title"
      data-testid="getting-started-guide"
      className="border-b border-slate-200 bg-slate-100/60 dark:border-slate-800 dark:bg-slate-950"
    >
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">
            A simple start
          </p>
          <h2 id="getting-started-title" className="mt-2 text-3xl font-black tracking-tight">
            Getting started with Pinngro
          </h2>
          <p className="mt-2 text-base text-slate-600 dark:text-slate-300">
            Everything you need to get up and running.
          </p>
        </div>

        <ol className="mt-8 grid gap-4 md:grid-cols-3">
          {steps.map((step) => (
            <li
              key={step.id}
              data-testid={`getting-started-step-${step.id}`}
              className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900"
            >
              <p className="text-sm font-black tracking-[0.16em] text-blue-600 dark:text-blue-400">
                {step.number}
              </p>
              <h3 className="mt-2 text-lg font-black">{step.title}</h3>
              <p className="mt-2 min-h-12 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {step.summary}
              </p>

              <div className="mt-4 divide-y divide-slate-200 border-y border-slate-200 dark:divide-slate-700 dark:border-slate-700">
                {[
                  ['why', 'Why do I need this?', step.why],
                  ['how', 'How do I set it up?', step.how],
                ].map(([kind, label, content]) => {
                  const key = `${step.id}-${kind}`;
                  const panelId = `getting-started-${key}`;
                  const isExpanded = Boolean(expanded[key]);
                  return (
                    <div key={key}>
                      <button
                        id={`${panelId}-control`}
                        type="button"
                        aria-expanded={isExpanded}
                        aria-controls={panelId}
                        onClick={() => toggle(key)}
                        className="flex min-h-11 w-full items-center justify-between gap-3 py-2 text-left text-sm font-bold text-slate-700 outline-none hover:text-blue-700 focus-visible:rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:text-slate-200 dark:hover:text-blue-300 dark:focus-visible:ring-offset-slate-900"
                      >
                        <span>{label}</span>
                        <FaChevronDown
                          aria-hidden="true"
                          className={`shrink-0 text-xs ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {isExpanded && (
                        <div
                          id={panelId}
                          role="region"
                          aria-labelledby={`${panelId}-control`}
                          className="pb-4 text-sm leading-6 text-slate-600 dark:text-slate-300"
                        >
                          {content}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default GettingStartedGuide;
